import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useHistory, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import ArticlePreview from './components/ArticlePreview';
import GenerationProgress from './components/GenerationProgress';
import FetchProgress from './components/FetchProgress';
import ArticleHistory from './components/ArticleHistory';
import Logo from '../../common/Logo';
import toolsConfig from '../../../config/tools';

/**
 * Article to Media Tool - Main Page
 * Users submit URLs to generate audio, social posts, and TLDR
 */
const ArticleToMediaMain = ({ auth }) => {
  const history = useHistory();
  const location = useLocation();

  // Determine if this is the tenant app version or free version
  const isTenantApp = location.pathname.startsWith('/app');
  const isFreeToolPath = location.pathname.startsWith('/tools') || location.pathname.startsWith('/free');

  // Redirect authenticated users away from free tool to dashboard
  useEffect(() => {
    if (auth && auth.id && isFreeToolPath) {
      history.replace('/dashboard');
    }
  }, [auth, isFreeToolPath, history]);

  // Check for URL in query params (from dashboard redirect)
  const queryParams = new URLSearchParams(location.search);
  const urlFromQuery = queryParams.get('url');

  const [url, setUrl] = useState(urlFromQuery || '');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [toolAvailable, setToolAvailable] = useState(true);
  const [freeArticlesUsed, setFreeArticlesUsed] = useState(() => {
    return parseInt(sessionStorage.getItem('articleToolFreeCount') || '0', 10);
  });
  const [freeArticleLimit, setFreeArticleLimit] = useState(() => {
    return parseInt(sessionStorage.getItem('articleToolFreeLimit') || '20', 10);
  });
  const hasReachedFreeLimit = freeArticlesUsed >= freeArticleLimit;
  const [autoStarted, setAutoStarted] = useState(false);
  const [showExtendedMessage, setShowExtendedMessage] = useState(false);

  // Processing states
  const [step, setStep] = useState('input'); // input, fetching, preview, processing
  const [fetchedHtml, setFetchedHtml] = useState('');
  const [fetchMethod, setFetchMethod] = useState('simple');
  const [progress, setProgress] = useState({});
  const [audioMode, setAudioMode] = useState('summary');
  const [fetchProgress, setFetchProgress] = useState({});
  const [extractedMetadata, setExtractedMetadata] = useState(null);
  const [extractionError, setExtractionError] = useState(null);
  const [creditInfo, setCreditInfo] = useState(null); // Track credit fallback info

  // Get tool config
  const toolConfig = toolsConfig.tools['article-to-media'];

  // API endpoints based on context (tenant app vs free tool)
  const apiEndpoints = {
    checkCredits: isTenantApp ? '/api/tenant/tools/article/check-credits' : '/api/tools/article/check-credits',
    checkUrl: '/api/tools/article/check-url', // Same for both
    fetchStream: '/api/tools/article/fetch-stream', // Same for both
    confirmStream: isTenantApp ? '/api/tenant/tools/article/confirm-stream' : '/api/tools/article/confirm-stream'
  };

  // Check if tool is available on mount
  useEffect(() => {
    checkToolAvailability();
  }, [isTenantApp]);

  // Auto-start processing if URL came from query params (dashboard redirect)
  useEffect(() => {
    if (urlFromQuery && !autoStarted && toolAvailable && step === 'input') {
      setAutoStarted(true);
      // Start immediately - no delay needed
      processUrl();
    }
  }, [urlFromQuery, autoStarted, toolAvailable, step]);

  // Redirect tenant app users to dashboard if no URL provided
  useEffect(() => {
    if (isTenantApp && !urlFromQuery && step === 'input' && !loading) {
      // Only redirect if not in a processing state
      history.replace('/dashboard');
    }
  }, [isTenantApp, urlFromQuery, step, loading, history]);

  // Show extended message after 60 seconds of processing
  useEffect(() => {
    if (step === 'fetching' || step === 'processing' || (loading && step === 'input')) {
      setShowExtendedMessage(false); // Reset when starting
      const timer = setTimeout(() => {
        setShowExtendedMessage(true);
      }, 60000);
      return () => clearTimeout(timer);
    }
  }, [step, loading]);

  const checkToolAvailability = async (emailToCheck) => {
    try {
      const response = await fetch(apiEndpoints.checkCredits, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // Include cookies for tenant auth
        body: JSON.stringify({ email: emailToCheck })
      });
      const data = await response.json();
      // Sync article usage counts from server
      if (data.articleLimit !== undefined) {
        setFreeArticleLimit(data.articleLimit);
        sessionStorage.setItem('articleToolFreeLimit', String(data.articleLimit));
      }
      if (data.articlesUsed !== undefined) {
        setFreeArticlesUsed(data.articlesUsed);
        sessionStorage.setItem('articleToolFreeCount', String(data.articlesUsed));
      }
      if (!data.hasCredits && data.error) {
        if (data.error.includes('temporarily unavailable') || data.error.includes('not enabled')) {
          setToolAvailable(false);
        }
        return { hasCredits: false, error: data.error };
      }
      return { hasCredits: true, balance: data.balance };
    } catch (err) {
      console.error('Error checking tool availability:', err);
      return { hasCredits: true }; // Allow attempt on error
    }
  };

  const validateEmail = (emailVal) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailVal);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!url.trim()) {
      setError('Please enter a URL');
      return;
    }

    // Validate URL format
    try {
      new URL(url);
    } catch (err) {
      setError('Please enter a valid URL');
      return;
    }

    // Check URL length
    if (url.length > 2000) {
      setError('URL is too long (max 2000 characters)');
      return;
    }

    // Validate email (not required for tenant app users)
    if (!isTenantApp) {
      if (!email.trim()) {
        setError('Please enter your email address');
        return;
      }

      if (!validateEmail(email)) {
        setError('Please enter a valid email address');
        return;
      }
    }

    await processUrl();
  };

  const processUrl = async () => {
    // Check if already reached free article limit this session (only for free tool, not tenant app)
    if (!isTenantApp && hasReachedFreeLimit) {
      // Form shouldn't be visible, but safety check
      return;
    }

    setLoading(true);
    setError('');
    setCreditInfo(null);

    try {
      // First check credits BEFORE doing anything else
      const creditResponse = await fetch(apiEndpoints.checkCredits, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: isTenantApp ? undefined : email })
      });
      const creditData = await creditResponse.json();

      // Sync article usage counts from server
      if (creditData.articleLimit !== undefined) {
        setFreeArticleLimit(creditData.articleLimit);
        sessionStorage.setItem('articleToolFreeLimit', String(creditData.articleLimit));
      }
      if (creditData.articlesUsed !== undefined) {
        setFreeArticlesUsed(creditData.articlesUsed);
        sessionStorage.setItem('articleToolFreeCount', String(creditData.articlesUsed));
      }

      if (!creditData.hasCredits) {
        if (creditData.code === 'EMAIL_LIMIT_REACHED') {
          // Count is already synced above
        } else if (creditData.code === 'INSUFFICIENT_TOKENS') {
          setError(creditData.error || 'Your organization has run out of tokens.');
        } else if (creditData.code === 'NO_TENANT_CONTEXT' || creditData.error?.includes('tenant')) {
          setError('Your account is not associated with an organization. Please contact support to get set up.');
        } else {
          setError(creditData.error || 'Tool is currently unavailable. Please try again later.');
        }
        setLoading(false);
        return;
      }

      // Check if URL already exists
      const checkResponse = await fetch(apiEndpoints.checkUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ url, email: isTenantApp ? undefined : email })
      });
      const checkData = await checkResponse.json();

      if (checkData.error) {
        setError(checkData.error);
        setLoading(false);
        return;
      }

      if (checkData.exists) {
        // Redirect to existing article (use correct path based on context)
        const existingPath = isTenantApp ? `/app/article-to-media/${checkData.article.slug}` : `/free/${checkData.article.slug}`;
        history.push(existingPath);
        return;
      }

      // Handle pending domain - user will be notified when approved
      if (checkData.pendingDomain) {
        setError(checkData.message || 'This domain is pending approval. You will be notified by email when it is approved.');
        setLoading(false);
        return;
      }

      // Switch to fetching step with progress display
      setStep('fetching');
      setFetchProgress({});
      setLoading(false);

      // Use streaming fetch for progress updates
      const response = await fetch(apiEndpoints.fetchStream, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ url, email: isTenantApp ? undefined : email })
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      // Helper to process SSE lines
      let currentEvent = null;
      const processLines = (lines) => {
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7).trim();
          } else if (line.startsWith('data: ') && currentEvent) {
            try {
              const data = JSON.parse(line.slice(6));

              if (currentEvent === 'progress') {
                setFetchProgress(prev => ({
                  ...prev,
                  [data.step]: { status: data.status, message: data.message }
                }));
              } else if (currentEvent === 'complete') {
                // Fetch complete - show preview
                setFetchedHtml(data.html);
                setFetchMethod(data.fetchMethod);
                setExtractedMetadata(data.extractedMetadata);
                setExtractionError(data.extractionError);
                setStep('preview');
                return true; // Signal to stop processing
              } else if (currentEvent === 'error') {
                setError(data.error);
                setStep('input');
                return true; // Signal to stop processing
              }
            } catch (e) {
              console.error('Error parsing SSE data:', e);
            }
            currentEvent = null;
          }
        }
        return false; // Continue processing
      };

      while (true) {
        const { done, value } = await reader.read();

        if (value) {
          buffer += decoder.decode(value, { stream: true });
        }

        // Parse SSE events from buffer
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        if (processLines(lines)) {
          return; // Complete or error event received
        }

        if (done) {
          // Process any remaining buffer content
          if (buffer.trim()) {
            const finalLines = buffer.split('\n');
            processLines(finalLines);
          }
          break;
        }
      }
    } catch (err) {
      setError('Failed to fetch article. Please try again.');
      setStep('input');
    }
  };

  const handleConfirm = async () => {
    setStep('processing');
    setError('');
    setProgress({});

    try {
      const response = await fetch(apiEndpoints.confirmStream, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          url,
          email: isTenantApp ? undefined : email,
          html: fetchedHtml,
          fetchMethod,
          extractedMetadata: extractedMetadata || undefined
        })
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      // Helper to process SSE lines
      let currentEvent = null;
      const processLines = (lines) => {
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7).trim();
          } else if (line.startsWith('data: ') && currentEvent) {
            try {
              const data = JSON.parse(line.slice(6));

              if (currentEvent === 'audio_mode') {
                setAudioMode(data.audioMode);
              } else if (currentEvent === 'progress') {
                setProgress(prev => ({
                  ...prev,
                  [data.step]: { status: data.status, ...data }
                }));
              } else if (currentEvent === 'complete') {
                // Increment free article usage count (only for free tool)
                if (!isTenantApp) {
                  const newCount = freeArticlesUsed + 1;
                  sessionStorage.setItem('articleToolFreeCount', String(newCount));
                  setFreeArticlesUsed(newCount);
                }
                // Redirect to appropriate path based on whether in tenant app or free tool
                const resultPath = isTenantApp ? `/app/article-to-media/${data.slug}` : `/free/${data.slug}`;
                history.push(resultPath);
                return true; // Signal to stop processing
              } else if (currentEvent === 'error') {
                // Check if this is an email limit error
                if (data.code === 'EMAIL_LIMIT_REACHED') {
                  setFreeArticlesUsed(freeArticleLimit);
                  sessionStorage.setItem('articleToolFreeCount', String(freeArticleLimit));
                  setStep('input');
                } else {
                  setError(data.error);
                  setStep('preview');
                }
                return true; // Signal to stop processing
              }
            } catch (e) {
              console.error('Error parsing SSE data:', e);
            }
            currentEvent = null;
          }
        }
        return false; // Continue processing
      };

      while (true) {
        const { done, value } = await reader.read();

        if (value) {
          buffer += decoder.decode(value, { stream: true });
        }

        // Parse SSE events from buffer
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        if (processLines(lines)) {
          return; // Complete or error event received
        }

        if (done) {
          // Process any remaining buffer content
          if (buffer.trim()) {
            const finalLines = buffer.split('\n');
            processLines(finalLines);
          }
          break;
        }
      }
    } catch (err) {
      // Check if this is a network/timeout error
      if (err.name === 'TypeError' || err.message.includes('network') || err.message.includes('Failed to fetch')) {
        setError(
          'Processing is taking longer than expected. Your article may still be generating. ' +
          'Check the "Recently Generated" section below or refresh the page in a minute to see your results.'
        );
        setStep('input');
        setUrl('');
      } else {
        setError('Failed to process article. Please try again.');
        setStep('preview');
      }
    }
  };

  const handleBackToInput = () => {
    setStep('input');
    setError('');
  };

  // Input styles
  const inputStyle = {
    padding: '0.875rem 1rem',
    borderRadius: '10px',
    border: '2px solid #e2e8f0',
    fontSize: '1rem',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    background: 'white'
  };

  const inputFocusStyle = {
    borderColor: '#6366f1',
    boxShadow: '0 0 0 3px rgba(99, 102, 241, 0.1)',
    outline: 'none'
  };

  if (!toolAvailable) {
    return (
      <>
        <Helmet>
          <title>{toolConfig.fullTitle}</title>
        </Helmet>
        <div style={{
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
          padding: '120px 0 4rem'
        }}>
          <Container>
            <Alert variant="warning" style={{
              background: 'rgba(245, 158, 11, 0.1)',
              border: '1px solid rgba(245, 158, 11, 0.3)',
              color: 'white',
              borderRadius: '12px'
            }}>
              This tool is temporarily unavailable. Please check back later.
            </Alert>
          </Container>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{toolConfig.fullTitle}</title>
        <meta name="description" content={toolConfig.description} />
        <meta name="robots" content="index, follow" />
      </Helmet>

      {/* Hero Header Background */}
      <div style={{
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        padding: (isTenantApp && step === 'input') ? '80px 0 4rem' : '100px 0 3rem',
        marginBottom: '0'
      }}>
      {/* Content only for free tool or processing steps */}
      {(!isTenantApp || step !== 'input') && (
          <Container>
            <Row className="justify-content-center">
              <Col lg={8} className="text-center">
                {/* Icon */}
                <div style={{
                  width: '64px',
                  height: '64px',
                  margin: '0 auto 1.5rem',
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {(step === 'fetching' || step === 'processing') ? (
                    <Spinner animation="border" variant="light" />
                  ) : step === 'preview' ? (
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                      <line x1="16" y1="13" x2="8" y2="13"></line>
                      <line x1="16" y1="17" x2="8" y2="17"></line>
                    </svg>
                  ) : (
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                      <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path>
                      <polyline points="13 2 13 9 20 9"></polyline>
                    </svg>
                  )}
                </div>

                {/* Title and Subtitle */}
                {step === 'fetching' ? (
                  <>
                    <h1 style={{ color: 'white', fontWeight: '700', fontSize: 'clamp(1.75rem, 3vw, 2.25rem)', marginBottom: '0.75rem' }}>
                      Fetching Article
                    </h1>
                    <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '1.1rem', marginBottom: '2rem' }}>
                      Retrieving and analyzing your article content
                    </p>
                  </>
                ) : step === 'preview' ? (
                  <>
                    <h1 style={{ color: 'white', fontWeight: '700', fontSize: 'clamp(1.75rem, 3vw, 2.25rem)', marginBottom: '0.75rem' }}>
                      Preview Article
                    </h1>
                    <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '1.1rem', marginBottom: '2rem' }}>
                      Verify this is the article you want to process
                    </p>
                  </>
                ) : step === 'processing' ? (
                  <>
                    <h1 style={{ color: 'white', fontWeight: '700', fontSize: 'clamp(1.75rem, 3vw, 2.25rem)', marginBottom: '0.75rem' }}>
                      Spawning Your Media
                    </h1>
                    <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '1.1rem', marginBottom: '2rem' }}>
                      Transforming one article into multiple formats
                    </p>
                  </>
                ) : (
                  <>
                    <h1 style={{ color: 'white', fontWeight: '700', fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', marginBottom: '0.75rem' }}>
                      {toolsConfig.landing.title}
                    </h1>
                    <p style={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: '1.1rem', marginBottom: '2rem' }}>
                      {toolsConfig.landing.subtitle}
                    </p>
                  </>
                )}

                {/* 3-Step Workflow Indicator */}
                <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem' }}>
                  {['Input Article', 'Fetch Content', 'Spawn Media'].map((stepName, index) => {
                    // preview means fetch completed, so step 2 should be green
                    const currentStepNum = step === 'input' ? 1 : step === 'fetching' ? 2 : step === 'preview' ? 3 : step === 'processing' ? 3 : 3;
                    const isCompleted = index + 1 < currentStepNum;
                    const isActive = index + 1 === currentStepNum;

                    return (
                      <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <div style={{
                          width: '28px',
                          height: '28px',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                          fontWeight: '600',
                          background: isCompleted
                            ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                            : isActive
                              ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
                              : 'rgba(255,255,255,0.2)',
                          color: '#fff'
                        }}>
                          {isCompleted ? (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                          ) : isActive && (step === 'fetching' || step === 'processing') ? (
                            <Spinner animation="border" size="sm" style={{ width: '12px', height: '12px' }} />
                          ) : (
                            index + 1
                          )}
                        </div>
                        <span style={{
                          color: isCompleted || isActive ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.5)',
                          fontSize: '0.85rem',
                          fontWeight: isActive ? '600' : '500'
                        }}>
                          {stepName}
                        </span>
                        {index < 2 && (
                          <div style={{
                            width: '24px',
                            height: '2px',
                            background: isCompleted ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : 'rgba(255,255,255,0.2)',
                            marginLeft: '0.5rem'
                          }} />
                        )}
                      </div>
                    );
                  })}
                </div>
              </Col>
            </Row>
          </Container>
        )}
      </div>

      {/* Main Content - overlaps the header for tenant users */}
      <Container style={{
        paddingTop: '2rem',
        paddingBottom: '5rem',
        marginTop: (isTenantApp && step === 'input') ? '-3rem' : '0'
      }}>
        {step === 'fetching' ? (
          <FetchProgress progress={fetchProgress} creditInfo={creditInfo} />
        ) : step === 'processing' ? (
          <GenerationProgress progress={progress} creditInfo={creditInfo} audioMode={audioMode} />
        ) : step === 'preview' ? (
          <ArticlePreview
            htmlContent={fetchedHtml}
            url={url}
            onConfirm={handleConfirm}
            loading={loading}
            error={error}
            extractedMetadata={extractedMetadata}
            extractionError={extractionError}
            creditInfo={creditInfo}
          />
        ) : (
          <>
            <Row className="mb-4 justify-content-center" style={{ alignItems: 'stretch' }}>
              <Col lg={isTenantApp ? 8 : 7} style={{ display: 'flex' }}>
                <div style={{
                  background: 'white',
                  borderRadius: '16px',
                  boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                  width: '100%',
                  overflow: 'hidden'
                }}>
                  {/* Card header for tenant users - matches Recently Spawned style */}
                  {isTenantApp && (
                    <div style={{
                      padding: '1.25rem 1.5rem',
                      borderBottom: '1px solid #f1f5f9',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem'
                    }}>
                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '8px',
                        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Logo size={18} color="white" />
                      </div>
                      <h5 style={{ margin: 0, fontWeight: '600', color: '#1a1a2e' }}>Article to Media</h5>
                    </div>
                  )}

                  {/* Form content */}
                  <div style={{ padding: isTenantApp ? '1.5rem' : '2rem' }}>
                  {/* Show loading when auto-starting from dashboard */}
                  {isTenantApp && urlFromQuery && step === 'input' ? (
                    <div style={{ textAlign: 'center', padding: '2rem 0' }}>
                      {error ? (
                        <>
                          <Alert variant="danger" style={{
                            background: 'rgba(239, 68, 68, 0.1)',
                            border: '1px solid rgba(239, 68, 68, 0.2)',
                            color: '#dc2626',
                            borderRadius: '10px',
                            marginBottom: '1.5rem',
                            textAlign: 'left'
                          }}>
                            {error}
                          </Alert>
                          <Button
                            variant="outline-secondary"
                            onClick={() => history.push('/dashboard')}
                          >
                            Back to Dashboard
                          </Button>
                        </>
                      ) : (
                        <>
                          <Spinner animation="border" style={{ color: '#6366f1', marginBottom: '1rem' }} />
                          <p style={{ color: '#64748b', margin: 0 }}>
                            {showExtendedMessage
                              ? "Still working on it — larger articles may take a bit longer. Thanks for your patience!"
                              : "Starting article extraction..."}
                          </p>
                        </>
                      )}
                    </div>
                  ) : !isTenantApp && hasReachedFreeLimit ? (
                    // User has reached their free article limit (only applies to free tool)
                    <div style={{ textAlign: 'center', padding: '1rem 0' }}>
                      <div style={{
                        width: '64px',
                        height: '64px',
                        margin: '0 auto 1.5rem',
                        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"></circle>
                          <line x1="12" y1="8" x2="12" y2="12"></line>
                          <line x1="12" y1="16" x2="12.01" y2="16"></line>
                        </svg>
                      </div>
                      <h4 style={{ color: '#1a1a2e', fontWeight: '600', marginBottom: '0.75rem' }}>
                        You've Used All Your Free Articles
                      </h4>
                      <p style={{ color: '#64748b', marginBottom: '1.5rem', fontSize: '0.95rem' }}>
                        Thanks for trying Spawnry! You've used all {freeArticleLimit} free articles. Contact our sales team for a free account with unlimited access.
                      </p>
                      <Link
                        to="/contact"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.875rem 1.5rem',
                          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                          color: 'white',
                          borderRadius: '10px',
                          fontSize: '1rem',
                          fontWeight: '600',
                          textDecoration: 'none',
                          boxShadow: '0 4px 14px rgba(99, 102, 241, 0.4)'
                        }}
                      >
                        Get Free Account Access
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="5" y1="12" x2="19" y2="12"></line>
                          <polyline points="12 5 19 12 12 19"></polyline>
                        </svg>
                      </Link>
                      <p style={{ color: '#94a3b8', marginTop: '1.5rem', fontSize: '0.8rem' }}>
                        Browse the recently spawned articles below to see what Spawnry can do.
                      </p>
                    </div>
                  ) : (
                    // Normal form
                    <Form onSubmit={handleSubmit}>
                      <Form.Group className="mb-4">
                        <Form.Label style={{ fontWeight: '600', color: '#1a1a2e', marginBottom: '0.5rem' }}>
                          Article URL
                        </Form.Label>
                        <Form.Control
                          type="url"
                          placeholder="https://example.com/article"
                          value={url}
                          onChange={(e) => setUrl(e.target.value)}
                          disabled={loading}
                          style={inputStyle}
                          onFocus={(e) => Object.assign(e.target.style, inputFocusStyle)}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#e2e8f0';
                            e.target.style.boxShadow = 'none';
                          }}
                        />
                      </Form.Group>

                      {/* Email field only shown for free tool, not tenant app */}
                      {!isTenantApp && (
                        <Form.Group className="mb-4">
                          <Form.Label style={{ fontWeight: '600', color: '#1a1a2e', marginBottom: '0.5rem' }}>
                            Email Address
                          </Form.Label>
                          <Form.Control
                            type="email"
                            placeholder="you@example.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={loading}
                            style={inputStyle}
                            onFocus={(e) => Object.assign(e.target.style, inputFocusStyle)}
                            onBlur={(e) => {
                              e.target.style.borderColor = '#e2e8f0';
                              e.target.style.boxShadow = 'none';
                            }}
                          />
                        </Form.Group>
                      )}

                      {/* Free articles remaining indicator */}
                      {!isTenantApp && freeArticlesUsed > 0 && (
                        <div style={{
                          background: 'rgba(99, 102, 241, 0.05)',
                          border: '1px solid rgba(99, 102, 241, 0.15)',
                          borderRadius: '10px',
                          padding: '0.75rem 1rem',
                          marginBottom: '1rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"></circle>
                            <polyline points="12 6 12 12 16 14"></polyline>
                          </svg>
                          <span style={{ color: '#4f46e5', fontSize: '0.875rem', fontWeight: '500' }}>
                            {freeArticleLimit - freeArticlesUsed} of {freeArticleLimit} free articles remaining
                          </span>
                        </div>
                      )}

                      {error && (
                        <Alert variant="danger" style={{
                          background: 'rgba(239, 68, 68, 0.1)',
                          border: '1px solid rgba(239, 68, 68, 0.2)',
                          color: '#dc2626',
                          borderRadius: '10px',
                          marginBottom: '1.5rem'
                        }}>
                          {error}
                        </Alert>
                      )}

                      <Button
                        type="submit"
                        disabled={loading || !url.trim() || (!isTenantApp && !email.trim())}
                        style={{
                          width: '100%',
                          padding: '0.875rem 1.5rem',
                          fontWeight: '600',
                          fontSize: '1rem',
                          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                          border: 'none',
                          borderRadius: '10px',
                          transition: 'transform 0.2s, box-shadow 0.2s'
                        }}
                        onMouseOver={(e) => {
                          if (!loading) {
                            e.target.style.transform = 'translateY(-2px)';
                            e.target.style.boxShadow = '0 8px 24px rgba(99, 102, 241, 0.4)';
                          }
                        }}
                        onMouseOut={(e) => {
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = 'none';
                        }}
                      >
                        {loading ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Processing...
                          </>
                        ) : (
                          'Spawn Media'
                        )}
                      </Button>

                      {/* Terms notice only for free tool users */}
                      {!isTenantApp && (
                        <p style={{ color: '#94a3b8', marginTop: '1rem', fontSize: '0.8rem', textAlign: 'center' }}>
                          By using this tool, you agree to our{' '}
                          <Link to="/terms-of-service" style={{ color: '#6366f1' }}>Terms of Service</Link> and{' '}
                          <Link to="/privacy-policy" style={{ color: '#6366f1' }}>Privacy Policy</Link>
                        </p>
                      )}
                    </Form>
                  )}
                  </div>
                </div>
              </Col>

              {/* Side cards only for free tool users */}
              {!isTenantApp && (
                <Col lg={5} style={{ display: 'flex', flexDirection: 'column' }}>
                  <div style={{
                    background: 'linear-gradient(135deg, rgba(99,102,241,0.05) 0%, rgba(139,92,246,0.05) 100%)',
                    borderRadius: '16px',
                    padding: '1.5rem',
                    border: '1px solid rgba(99,102,241,0.1)',
                    marginBottom: '1rem'
                  }}>
                    <h4 style={{ color: '#1a1a2e', fontWeight: '600', marginBottom: '1rem', fontSize: '1.1rem' }}>
                      What you'll get:
                    </h4>
                    <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                      {[
                        { icon: '1', text: '5-bullet TLDR summary' },
                        { icon: '2', text: '3 unique social media posts' },
                        { icon: '3', text: 'AI-generated images for each post' },
                        { icon: '4', text: 'AI-generated audio summary' }
                      ].map((item, idx) => (
                        <li key={idx} style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          marginBottom: '0.75rem',
                          color: '#1a1a2e'
                        }}>
                          <span style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            flexShrink: 0
                          }}>
                            {item.icon}
                          </span>
                          <span style={{ fontSize: '0.95rem' }}>{item.text}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Contact Sales CTA */}
                  <div style={{
                    background: 'white',
                    borderRadius: '16px',
                    padding: '1.5rem',
                    border: '1px solid #e2e8f0',
                    textAlign: 'center',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}>
                    <h5 style={{ color: '#1a1a2e', fontWeight: '600', marginBottom: '0.5rem', fontSize: '1rem' }}>
                      Need more?
                    </h5>
                    <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '1rem' }}>
                      Add your team, track usage, and get dedicated support.
                    </p>
                    <Link to="/contact" style={{
                      display: 'inline-block',
                      padding: '0.625rem 1.25rem',
                      background: '#1a1a2e',
                      color: 'white',
                      borderRadius: '8px',
                      fontWeight: '600',
                      fontSize: '0.875rem',
                      textDecoration: 'none',
                      transition: 'background 0.2s'
                    }}>
                      Contact Sales
                    </Link>
                  </div>
                </Col>
              )}
            </Row>

            {/* Article History - same width as form for tenant users */}
            {isTenantApp ? (
              <Row className="justify-content-center">
                <Col lg={8}>
                  <ArticleHistory />
                </Col>
              </Row>
            ) : (
              <ArticleHistory />
            )}
          </>
        )}
      </Container>
    </>
  );
};

function mapStateToProps({ auth }) {
  return { auth };
}

export default connect(mapStateToProps)(ArticleToMediaMain);
